import { LegacyProductCode } from '@up2stay/up2promo-api-client';
import type { ProductBehavior } from '@up2stay/up2promo-api-client';

import { behaviorTypeToProductType } from './product-behavior-codes';
import type {
  ProductFormLocation,
  ProductFormValues,
} from './product-form.types';
import { getProductKindLabel } from './product-identity.utils';
import {
  buildProductWriteFromFormValues,
  canSubmitForRoutingMode,
  getLocationReadinessIssue,
  getLocationReadinessIssueForBehaviourType,
  getLocationRoutingWarning,
  getRoutingOptions,
  inferRoutingMode,
  isLegacyUrlOnlyProduct,
  isValidHttpUrl,
  type LocationReadinessIssue,
  type LocationRoutingWarning,
  type ProductRoutingMode,
} from './product-routing.utils';
import type { ProductKindLabels } from './products.constants';

export function getLocationReadinessIssueForForm(
  loc: ProductFormLocation,
  knownProductType?: string | null,
  targetKindForReadiness?: ProductBehavior | string,
): LocationReadinessIssue | null {
  if (knownProductType) {
    return getLocationReadinessIssue(knownProductType, loc);
  }
  if (targetKindForReadiness) {
    return getLocationReadinessIssueForBehaviourType(
      targetKindForReadiness,
      loc,
    );
  }
  return null;
}

function locationDisplayName(loc: ProductFormLocation): string {
  return loc.name?.trim() || loc.id;
}

function locationMatchesSearch(
  loc: ProductFormLocation,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [locationDisplayName(loc), loc.address ?? '', loc.id]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export type PartitionedProductFormLocations = {
  ready: ProductFormLocation[];
  incomplete: ProductFormLocation[];
};

/** Ready locations first, then incomplete; each group sorted by name. */
export function partitionProductFormLocations(
  locations: ProductFormLocation[],
  options: {
    search: string;
    locale: string;
    knownProductType?: string | null;
    targetKindForReadiness?: ProductBehavior | string;
  },
): PartitionedProductFormLocations {
  const collator = options.locale === 'de' ? 'de' : 'en';
  const sortByName = (a: ProductFormLocation, b: ProductFormLocation) =>
    locationDisplayName(a).localeCompare(locationDisplayName(b), collator, {
      sensitivity: 'base',
    });

  const filtered = locations.filter((loc) =>
    locationMatchesSearch(loc, options.search),
  );

  const ready: ProductFormLocation[] = [];
  const incomplete: ProductFormLocation[] = [];

  for (const loc of filtered) {
    const issue = getLocationReadinessIssueForForm(
      loc,
      options.knownProductType,
      options.targetKindForReadiness,
    );
    if (issue) incomplete.push(loc);
    else ready.push(loc);
  }

  ready.sort(sortByName);
  incomplete.sort(sortByName);

  return { ready, incomplete };
}

export function mapLocationsForForm(
  locations: Array<{
    id?: string;
    name?: string | null;
    address?: string | null;
    reviewExternalIds?: Record<string, string> | null;
    externalLinks?: Array<{ type?: string; value?: string }> | null;
  }>,
): ProductFormLocation[] {
  return locations
    .filter((l): l is typeof l & { id: string } => !!l.id)
    .map((l) => ({
      id: l.id,
      name: l.name,
      address: l.address ?? null,
      reviewExternalIds: l.reviewExternalIds ?? null,
      externalLinks: l.externalLinks ?? undefined,
    }));
}

export function shouldShowBehaviourSelector(
  knownProductType?: string | null,
): boolean {
  return !knownProductType || knownProductType === LegacyProductCode.CU;
}

export function resolveEffectiveProductType(
  knownProductType: string | undefined | null,
  targetKind: ProductBehavior | string,
): string {
  if (knownProductType && knownProductType !== LegacyProductCode.CU) {
    return knownProductType;
  }
  return behaviorTypeToProductType(targetKind);
}

export type ProductFormRoutingState = {
  routingMode: ProductRoutingMode;
  routingOptions: ProductRoutingMode[];
  showRoutingModeSelector: boolean;
  showBehaviourSelector: boolean;
  showProductKindReadonly: boolean;
  productKindLabel: string;
  showLegacyBanner: boolean;
  locationWarning: LocationRoutingWarning | null;
};

export function getProductFormRoutingState(params: {
  values: ProductFormValues;
  knownProductType?: string | null;
  locations: ProductFormLocation[];
  kindLabels?: ProductKindLabels;
  routingModeOverride?: ProductRoutingMode;
}): ProductFormRoutingState {
  const routingOptions = getRoutingOptions(params.knownProductType);
  const routingMode =
    params.routingModeOverride ??
    inferRoutingMode(params.values, params.knownProductType);
  const showBehaviourSelector = shouldShowBehaviourSelector(
    params.knownProductType,
  );
  const effectiveProductType = resolveEffectiveProductType(
    params.knownProductType,
    params.values.targetKind,
  );

  const selectedLocation = params.locations.find(
    (l) => l.id === params.values.locationId,
  );

  return {
    routingMode,
    routingOptions,
    showRoutingModeSelector:
      routingOptions.length > 1 && !showBehaviourSelector,
    showBehaviourSelector,
    showProductKindReadonly: !showBehaviourSelector,
    productKindLabel: getProductKindLabel(
      params.knownProductType ?? effectiveProductType,
      params.kindLabels,
    ),
    showLegacyBanner: isLegacyUrlOnlyProduct(
      params.knownProductType,
      params.values.targetKind,
      params.values.targetUrl,
    ),
    locationWarning: getLocationRoutingWarning(
      routingMode,
      params.knownProductType ?? effectiveProductType,
      selectedLocation,
    ),
  };
}

export function canSubmitProductForm(params: {
  description: string;
  values: ProductFormValues;
  knownProductType?: string | null;
  locations?: ProductFormLocation[];
  routingMode?: ProductRoutingMode;
  instagramUsername?: string;
  tiktokUsername?: string;
  isSaving?: boolean;
  descriptionOptional?: boolean;
}): boolean {
  const mode =
    params.routingMode ??
    inferRoutingMode(params.values, params.knownProductType);
  if (
    !canSubmitForRoutingMode({
      description: params.description,
      mode,
      values: params.values,
      knownProductType: params.knownProductType,
      locations: params.locations,
      instagramUsername: params.instagramUsername,
      tiktokUsername: params.tiktokUsername,
      isSaving: params.isSaving,
      descriptionOptional: params.descriptionOptional,
    })
  ) {
    return false;
  }

  const { target } = buildProductWriteFromFormValues(
    params.values,
    params.knownProductType,
    mode,
  );
  if (target.url) {
    return isValidHttpUrl(target.url);
  }

  return true;
}
